// Global
import React from 'react';

// Local
import styles from './RecipeDetail.module.scss';
import { KitchenTip, RecipeResponse } from 'lib/services/models/content-response';
import { RecipeHero } from './RecipeHero/RecipeHero';
import { RecipeDescriptionSeoContentSection } from './RecipeDescriptionSeoContentSection/RecipeDescriptionSeoContentSection';
import { RecipeHeader, extractRecipeHeaderData } from './RecipeHeader';
import { RecipeIngredientInstructionsSection } from './RecipeIngredientInstructionsSection/RecipeIngredientInstructionsSection';
import { extractRecipeHeroData } from './RecipeHero/RecipeHero.utils';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { extractRecipeFAQData } from './RecipeDescriptionSeoContentSection/RecipeFAQ/RecipeFAQ.utils';
import { extractRecipeSeoContentData } from './RecipeDescriptionSeoContentSection/RecipeSeo/RecipeSeo.utils';
import { Contributor } from 'lib/gql/getContributor';
import useDictionary from 'lib/hooks/useDictionary';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { Badge } from '../../content/Badge/Badge';

export interface RecipeDetailProps {
  recipe?: RecipeResponse;
  contributor?: Contributor;
  videoTranscriptUrls?: Record<string, string | undefined>;
  videoWithAudioDescriptionURL?: string;
  siteSettings?: SiteSettings | null;
}

export function RecipeDetail(props: RecipeDetailProps) {
  const { recipe, contributor, videoTranscriptUrls, videoWithAudioDescriptionURL, siteSettings } =
    props;

  const { getDictionaryValue } = useDictionary();
  const { locale, siteName } = useGlobalData();

  // Create dictionary object with translated values to avoid hydration issues
  const dictionary = {
    kitchenTested:
      getDictionaryValue('Components.Recipe.RecipeHero.KitchenTested') || 'Kitchen Tested',
    prepTime: getDictionaryValue('Components.Recipe.Attributes.PrepTime') || 'Prep Time:',
    totalTime: getDictionaryValue('Components.Recipe.Attributes.TotalTime') || 'Total Time:',
    ingredients: getDictionaryValue('Components.Recipe.Attributes.Ingredients') || 'Ingredients:',
    servings: getDictionaryValue('Components.Recipe.Attributes.Servings') || 'Servings:',
    caloriesPerServing:
      getDictionaryValue('Components.Recipe.Attributes.CaloriesPerServing') ||
      'Calories Per Serving:',
    by: getDictionaryValue('Common.ByLine.By') || 'By',
    published: getDictionaryValue('Common.ByLine.Published') || 'Published',
    modifiedDate: getDictionaryValue('Common.ByLine.Modified') || 'Updated',
  };

  if (!recipe) {
    return <div>No recipe found</div>;
  }

  const seoContent: KitchenTip[] = [];
  const faqContent: KitchenTip[] = [];
  const recipeTips: KitchenTip[] = [];
  const moreAboutRecipe: KitchenTip[] = [];
  const seoContentSettings = { ...extractRecipeSeoContentData(siteSettings).seoContentSettings };
  const faqSettings = { ...extractRecipeFAQData(siteSettings).faqSettings };
  recipe.KitchenTips?.forEach((tip) => {
    if (tip.Category.startsWith('SEO Content')) {
      seoContent.push(tip);
    } else if (tip.Category.startsWith('Editorial')) {
      moreAboutRecipe.push(tip);
    } else if (tip.Category.startsWith('FAQ')) {
      faqContent.push(tip);
    } else if (!tip.Category.startsWith('SEO Content') && !tip.Category.startsWith('FAQ')) {
      recipeTips.push(tip);
    }
  });

  return (
    <div className={`${styles.RecipeCardWrapper}`}>
      <div className={styles.RecipeCard}>
        <RecipeHero
          {...extractRecipeHeroData(recipe, siteSettings, contributor, locale, dictionary)}
        />

        <Badge recipe={recipe} siteName={siteName} siteSettings={siteSettings} />

        <RecipeDescriptionSeoContentSection
          recipe={recipe}
          seoContent={seoContent}
          faqContent={faqContent}
          faqSettings={faqSettings}
          moreAboutRecipe={moreAboutRecipe}
          seoContentSettings={seoContentSettings}
        />
        <RecipeHeader
          {...extractRecipeHeaderData(
            recipe,
            siteSettings,
            videoTranscriptUrls,
            videoWithAudioDescriptionURL
          )}
        />
        <RecipeIngredientInstructionsSection
          recipe={recipe}
          recipeTips={recipeTips}
          siteSettings={siteSettings}
        />
      </div>
    </div>
  );
}
